import React, { useEffect, useState, useCallback } from 'react';
import { createYearDepreciation, lockFinYearDepreciation, getFinancialYearList } from '../../services/it-depreciation.service';
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import '../ExcelUploads/AssetInfo/AssetDepreciation.css';
import * as CryptoJS from "crypto-js";
import FromFieldDateNoLabel from "../../atoms/FromFieldDateNoLabel";

// Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function CreateITYear() {
    const [financialYears, setFinancialYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [errMsg, setErrMsg] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [modalMsg, setModalMsg] = useState({ text: '', type: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFYData, setSelectedFYData] = useState({ fyYear: '', fyName: '', startDate: '', endDate: '', cutoffDate: '', isESD: 'N', totalDays: 365 });
    const [isEditMode, setIsEditMode] = useState(false);

    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    const [finalizeData, setFinalizeData] = useState({
        fyName: '',
        normalStatus: 'Not Generated', // Ye backend status se aayenge
        doubleStatus: 'Generated and Pending',
        tripleStatus: 'Generated & Finalized'
    });

    const handleFinalize = (data) => {
        setModalMsg({ text: 'Finalize', type: '' });
        setFinalizeData({
            fyName: data.financialYearName,
            // Yahan aap backend se aaye huye actual status use karenge
            normalStatus: data.normal_dep_status || 'Not Generated',
            doubleStatus: data.double_dep_status || 'Generated & Pending',
            tripleStatus: data.triple_dep_status || 'Generated & Finalized'
        });
        setShowFinalizeModal(true);
    };
    const handleEdit = (data) => {
        setIsEditMode(true);
        setModalMsg({ text: 'Edit Depreciation Settings', type: '' });
        setIsSubmitting(false);

        // Date strings ko Date object mein badalna zaroori hai MUI DatePicker ke liye
        setSelectedFYData({
            fyYear: data.financialYear,
            fyName: data.financialYearName,
            startDate: data.start_date ? new Date(data.start_date) : '',
            cutoffDate: data.cutoff_date ? new Date(data.cutoff_date) : '',
            endDate: data.end_date ? new Date(data.end_date) : '',
            isESD: data.is_esd || 'N',
            totalDays: data.working_days || 365
        });
        setShowModal(true);
    };

    const fetchFinancialYears = useCallback(async () => {
        try {
            setLoading(true);
            const EncryptData = CryptoJS.AES.encrypt(
                JSON.stringify({ authToken: sessionStorage.getItem('LoggedInToken') }),
                process.env.REACT_APP_ENCRYPT_SECRET_KEY
            ).toString();

            const res = await getFinancialYearList({ token: EncryptData });
            // Note: Make sure backend returns 'creation_flag' and 'lock_status' from year_depreciation table
            if (res?.data?.data?.length) {
                setFinancialYears(res.data.data);
            }
        } catch (err) {
            setErrMsg("Failed to load financial years");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFinancialYears();
    }, [fetchFinancialYears]);

    const handleCreateFY = (fyYear, fyName) => {
        const start = new Date(fyYear, 3, 1);
        const cutoff = new Date(fyYear, 9, 2);
        const end = new Date(parseInt(fyYear) + 1, 2, 31);

        setModalMsg({ text: '', type: '' });
        setIsSubmitting(false);

        setSelectedFYData({
            fyYear: fyYear,
            fyName: fyName,
            startDate: start,
            cutoffDate: cutoff,
            endDate: end,
            isESD: 'N',
            totalDays: 365
        });
        setShowModal(true);
    };

    const createFinancialYear = async () => {
        const formatDate = (dateObj) => {
            if (!dateObj || !(dateObj instanceof Date)) return dateObj;
            let d = dateObj.getDate();
            let m = dateObj.getMonth() + 1;
            let y = dateObj.getFullYear();
            return `${y}-${m < 10 ? '0' + m : m}-${d < 10 ? '0' + d : d}`;
        };

        try {
            setIsSubmitting(true);
            const obj = {
                year: selectedFYData.fyYear,
                fy_year: selectedFYData.fyName,
                start_date: formatDate(selectedFYData.startDate),
                end_date: formatDate(selectedFYData.endDate),
                cutoff_date: formatDate(selectedFYData.cutoffDate),
                is_esd: selectedFYData.isESD,
                working_days: selectedFYData.totalDays,
                creation_flag: 'Y',
                lock_status: 'N',
                company_id: sessionStorage.getItem('LoggedInCompanyId'),
                division_id: sessionStorage.getItem('LoggedInDivisionId'),
                created_by: sessionStorage.getItem('LoggedInUserId'),
                mode: isEditMode ? 'EDIT' : 'CREATE'
            };

            let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
                'param_data': obj,
                'authToken': sessionStorage.getItem('LoggedInToken'),
            }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();

            const res = await createYearDepreciation({ token: EncryptData });

            if (res?.data?.success) {
                setModalMsg({ text: `Success: Data ${isEditMode ? 'Updated' : 'Generated'}!`, type: 'success' });
                fetchFinancialYears();
            } else {
                setModalMsg({ text: res.data.message || "Failed", type: 'error' });
                setIsSubmitting(false);
            }
        } catch (err) {
            setModalMsg({ text: "Server Error", type: 'error' });
            setIsSubmitting(false);
        }
    };

    const handleLockUnlock = async (data, status) => {
        confirmAlert({
            title: status === 'Y' ? "Lock Financial Year" : "Unlock Financial Year",
            message: `Are you sure you want to ${status === 'Y' ? 'lock' : 'unlock'} ${data.financialYearName}?`,
            buttons: [
                {
                    label: "Yes",
                    onClick: async () => {
                        try {
                            const obj = {
                                fy_year: data.financialYearName,
                                lock_status: status,
                                company_id: sessionStorage.getItem('LoggedInCompanyId'),
                                division_id: sessionStorage.getItem('LoggedInDivisionId'),
                            };
                            let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
                                'param_data': obj,
                                'authToken': sessionStorage.getItem('LoggedInToken'),
                            }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();

                            const res = await lockFinYearDepreciation({ token: EncryptData });
                            if (res?.data?.success) {
                                setSuccess(`Year ${status === 'Y' ? 'Locked' : 'Unlocked'} successfully`);
                                fetchFinancialYears();
                            }
                        } catch (err) { setErrMsg("Action failed"); }
                    }
                },
                { label: "No" }
            ]
        });
    };

    // --- Sub-Component: Card ---
    const FYCard = ({ fyYear, data }) => {
        console.log(`Data for ${fyYear}:`, data);
        const isCreated = data?.creation_flag === "Y";
        const isLocked = data?.lock_status === "Y";

        const cardStyle = {
            background: '#fff', borderRadius: '12px', padding: '20px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            borderTop: `6px solid ${isLocked ? '#dc3545' : isCreated ? '#28a745' : '#ffc107'}`,
            height: '100%', display: 'flex', flexDirection: 'column'
        };

        return (
            <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <div style={cardStyle}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <CalendarMonthIcon style={{ color: '#555' }} />
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>{fyYear}</h3>
                        </div>
                        {isLocked ? <LockIcon style={{ color: '#dc3545' }} /> :
                            isCreated ? <CheckCircleIcon style={{ color: '#28a745' }} /> : null}
                    </div>

                    <p style={{ fontSize: '0.85rem', color: '#666' }}>
                        Status: <strong>{isLocked ? 'Locked' : isCreated ? 'Generated' : 'Pending'}</strong>
                    </p>

                    <div className="mt-auto d-flex flex-column gap-2">
                        {isLocked ? (
                            <button onClick={() => handleLockUnlock(data, 'N')} className="btn btn-outline-danger btn-sm w-100">
                                <LockOpenIcon fontSize="inherit" /> Unlock FY
                            </button>
                        ) : isCreated ? (
                            <>
                                <div className="d-flex gap-2">
                                    {/* Edit button triggers handleEdit with data */}
                                    <button onClick={() => handleEdit(data)} className="btn btn-primary btn-sm flex-fill">Edit</button>
                                    <button onClick={() => handleFinalize(data)} className="btn btn-info btn-sm flex-fill text-white">Finalize</button>
                                </div>
                                <button onClick={() => handleLockUnlock(data, 'Y')} className="btn btn-dark btn-sm">
                                    <LockIcon fontSize="inherit" /> Lock Year
                                </button>
                            </>
                        ) : (
                            <button onClick={() => handleCreateFY(data.financialYear, fyYear)} className="btn btn-success btn-sm w-100">
                                <AddCircleIcon fontSize="inherit" /> Create Year Depreciation
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='container-fluid py-4 bg-light' style={{ minHeight: '100vh' }}>
            {/* Header */}
            <div className="bg-white p-4 rounded shadow-sm mb-4 d-flex align-items-center gap-3">
                <CalendarMonthIcon color="primary" style={{ fontSize: '2.5rem' }} />
                <h2 className="mb-0 fw-bold">Create IT Year</h2>
            </div>

            {/* Notifications */}
            {success && <div className="alert alert-success shadow-sm">{success}</div>}
            {errMsg && <div className="alert alert-danger shadow-sm">{errMsg}</div>}

            {/* Grid */}
            <div className="row justify-content-center">
                {financialYears.map((fy) => (
                    <FYCard key={fy.id} fyYear={fy.financialYearName} data={fy} />
                ))}
            </div>

            {/* --- MODAL --- */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200
                }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '600px', overflow: 'hidden' }}>
                        <div style={{
                            padding: '15px 25px',
                            backgroundColor: modalMsg.type === 'success' ? '#d1e7dd' : modalMsg.type === 'error' ? '#f8d7da' : '#fff',
                            borderBottom: '1px solid #dee2e6'
                        }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="fw-bold m-0">{modalMsg.text || "Generate Depreciation"}</h4>
                                <button className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                        </div>

                        <div style={{ padding: '25px' }}>
                            <table className="table table-bordered align-middle">
                                <tbody>
                                <tr>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>Financial Year</td>
                                    <td className="fw-bold">{selectedFYData.fyName}</td>
                                </tr>
                                {['startDate', 'cutoffDate', 'endDate'].map((field, idx) => (
                                    <tr key={field}>
                                        <td style={{fontWeight: 'bold'}}>
                                            {idx === 0 ? 'Start of FY' : idx === 1 ? 'Cutoff Date (180 Days)' : 'End of FY'}
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-end">
                                                <FromFieldDateNoLabel
                                                    inputValue={selectedFYData[field]}
                                                    setInputValue={(val) => setSelectedFYData({...selectedFYData, [field]: val})}
                                                    disabled={modalMsg.type === 'success'}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td style={{fontWeight: 'bold'}}>Is ESD Applicable</td>
                                    <td>
                                        <select className="form-select form-select-sm"
                                                value={selectedFYData.isESD}
                                                disabled={modalMsg.type === 'success'}
                                                onChange={(e) => setSelectedFYData({...selectedFYData, isESD: e.target.value})}>
                                            <option value="Y">Yes</option>
                                            <option value="N">No</option>
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{fontWeight: 'bold'}}>Total Working Days</td>
                                    <td>
                                        <input type="number" className="form-control form-control-sm"
                                               value={selectedFYData.totalDays}
                                               disabled={modalMsg.type === 'success'}
                                               onChange={(e) => setSelectedFYData({...selectedFYData, totalDays: e.target.value})} />
                                    </td>
                                </tr>
                                </tbody>
                            </table>

                            <div className="d-flex flex-column gap-2 mt-4">
                                <button
                                    className="btn btn-lg w-100 py-3"
                                    onClick={createFinancialYear}
                                    disabled={isSubmitting || modalMsg.type === 'success'}
                                    style={{
                                        backgroundColor: modalMsg.type === 'success' ? '#e9ecef' : '#b4c6e7',
                                        color: '#333', fontWeight: 'bold', border: 'none', borderRadius: '8px'
                                    }}
                                >
                                    {modalMsg.type === 'success' ? 'GENERATED' : 'GENERATE'}
                                </button>
                                <button className="btn btn-light w-100" onClick={() => setShowModal(false)}>
                                    {modalMsg.type === 'success' ? 'Close' : 'Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showFinalizeModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1200
                }}>
                    <div style={{background: '#fff', borderRadius: '12px', width: '600px', overflow: 'hidden'}}>
                        <div style={{
                            padding: '15px 25px',
                            backgroundColor: modalMsg.type === 'success' ? '#d1e7dd' : modalMsg.type === 'error' ? '#f8d7da' : '#fff',
                            borderBottom: '1px solid #dee2e6'
                        }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="fw-bold m-0">{modalMsg.text || "Finalize"}</h4>
                                <button className="btn-close" onClick={() => setShowFinalizeModal(false)}></button>
                            </div>
                        </div>

                        <div style={{padding: '25px'}}>
                            <table className="table table-bordered align-middle">
                                <tbody>
                                <tr>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>Financial Year</td>
                                    <td className="fw-bold">{finalizeData.fyName}</td>
                                </tr>
                                <tr>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>Normal Depreciation</td>
                                    <td className="fw-bold">Not Generated</td>
                                </tr>
                                <tr>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>Double Shift Depreciation</td>
                                    <td className="fw-bold">Generated and Pending</td>
                                </tr>
                                <tr>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>Triple Shift Depreciation</td>
                                    <td className="fw-bold">Generated & Finalized</td>
                                </tr>

                                </tbody>
                            </table>

                            <div className="d-flex flex-column gap-2 mt-4">
                                <button
                                    className="btn btn-lg w-100 py-3"
                                    onClick={createFinancialYear}
                                    disabled={isSubmitting || modalMsg.type === 'success'}
                                    style={{
                                        backgroundColor: modalMsg.type === 'success' ? '#e9ecef' : '#b4c6e7',
                                        color: '#333', fontWeight: 'bold', border: 'none', borderRadius: '8px'
                                    }}
                                >
                                    {modalMsg.type === 'success' ? 'FINALIZED' : 'FINALIZE'}
                                </button>
                                <button className="btn btn-light w-100" onClick={() => setShowFinalizeModal(false)}>
                                    {modalMsg.type === 'success' ? 'Close' : 'Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}